import { motion } from "framer-motion";

/**
 * The two shapes upload progress takes: a thin bar in the lesson dialog and a
 * ring small enough for the header chip. Both spring towards the new value
 * rather than jump, since acks arrive in bursts.
 */

const SPRING = { type: "spring", stiffness: 140, damping: 24 } as const;

export function ProgressBar({ value, active }: { value: number; active: boolean }) {
  const percent = Math.max(2, Math.min(100, value * 100));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      className="relative h-1.5 w-full overflow-hidden rounded-full bg-neutral-900/[0.07] dark:bg-white/[0.1]"
    >
      <motion.div
        initial={false}
        animate={{ width: `${percent}%` }}
        transition={SPRING}
        className={`absolute inset-y-0 left-0 overflow-hidden rounded-full bg-[#222] dark:bg-[#ededeb] ${active ? "" : "animate-pulse"}`}
      >
        {active && <div className="lls-shimmer absolute inset-0" />}
      </motion.div>
    </div>
  );
}

const RING_RADIUS = 6;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

export function ProgressRing({ value, className = "" }: { value: number; className?: string }) {
  const clamped = Math.max(0, Math.min(1, value));
  return (
    <svg viewBox="0 0 16 16" className={`-rotate-90 ${className}`} aria-hidden>
      <circle
        cx="8"
        cy="8"
        r={RING_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="2"
      />
      <motion.circle
        cx="8"
        cy="8"
        r={RING_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={RING_LENGTH}
        initial={false}
        animate={{ strokeDashoffset: RING_LENGTH * (1 - clamped) }}
        transition={SPRING}
      />
    </svg>
  );
}
